import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Lock, Loader2, LogOut, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("dm_admin_token") || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setLeads(res.data))
      .catch(() => {
        localStorage.removeItem("dm_admin_token");
        setToken("");
      });
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("dm_admin_token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setError("Incorrect password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("dm_admin_token");
    setToken("");
    setLeads(null);
    setPassword("");
  };

  return (
    <div className="min-h-screen bg-white text-black font-sans antialiased" data-testid="admin-page">
      <header className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="bg-brand-red text-white font-display text-lg leading-none px-2.5 py-1.5">DM</span>
            <span className="font-display uppercase tracking-wider text-xl">Enquiry Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <a data-testid="admin-back-link" href="/" className="text-xs font-bold tracking-[0.15em] uppercase text-black/50 hover:text-brand-red transition-colors flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4" /> Back to site
            </a>
            {token && (
              <button data-testid="admin-logout-btn" onClick={logout} className="text-xs font-bold tracking-[0.15em] uppercase text-black/50 hover:text-brand-red transition-colors flex items-center gap-1.5">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        {!token ? (
          <div className="max-w-md mx-auto mt-16">
            <div className="clip-corner bg-[#F7F7F7] border border-black/10 p-8">
              <span className="w-12 h-12 bg-brand-red clip-corner flex items-center justify-center">
                <Lock className="w-6 h-6 text-white" strokeWidth={2} />
              </span>
              <h1 className="mt-6 font-display uppercase text-4xl">Admin Sign In</h1>
              <p className="mt-2 text-sm text-black/55">Enter the dashboard password to view quote enquiries.</p>
              <form onSubmit={login} className="mt-6 space-y-5" data-testid="admin-login-form">
                <input
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  className="w-full bg-transparent border-b-2 border-black/15 focus:border-brand-red outline-none py-3 text-black placeholder:text-black/30 transition-colors duration-200"
                />
                {error && <p data-testid="admin-login-error" className="text-sm font-semibold text-brand-red">{error}</p>}
                <button
                  data-testid="admin-login-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red-dark disabled:opacity-70 text-white font-bold text-sm tracking-[0.2em] uppercase px-6 py-3.5 transition-colors duration-200"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-3 h-3 bg-brand-red" />
                  <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-black/60">Quote Enquiries</span>
                </div>
                <h1 className="font-display uppercase text-4xl md:text-5xl">
                  {leads ? `${leads.length} Enquir${leads.length === 1 ? "y" : "ies"}` : "Loading…"}
                </h1>
              </div>
            </div>

            {leads && leads.length === 0 && (
              <div data-testid="admin-empty-state" className="border border-dashed border-black/20 py-20 flex flex-col items-center gap-3 text-black/40">
                <Inbox className="w-8 h-8" strokeWidth={1.5} />
                <span className="text-sm font-semibold">No enquiries yet — new quote requests will appear here.</span>
              </div>
            )}

            {leads && leads.length > 0 && (
              <div className="overflow-x-auto border border-black/10" data-testid="admin-leads-table">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="bg-black text-white text-left">
                      {["Date", "Name", "Company", "Email", "Phone", "Message"].map((h) => (
                        <th key={h} className="px-4 py-3 text-[10px] font-bold tracking-[0.2em] uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, i) => (
                      <tr key={lead.id || i} data-testid={`admin-lead-row-${i + 1}`} className={i % 2 === 0 ? "bg-white" : "bg-[#F7F7F7]"}>
                        <td className="px-4 py-3 text-black/50 whitespace-nowrap">{formatDate(lead.created_at)}</td>
                        <td className="px-4 py-3 font-bold">{lead.name}</td>
                        <td className="px-4 py-3 text-black/70">{lead.company || "—"}</td>
                        <td className="px-4 py-3">
                          <a href={`mailto:${lead.email}`} className="text-brand-red hover:underline">{lead.email}</a>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <a href={`tel:${(lead.phone || "").replace(/\s/g, "")}`} className="text-brand-red hover:underline">{lead.phone}</a>
                        </td>
                        <td className="px-4 py-3 text-black/70 max-w-xs">{lead.message || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
